import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import PropTypes from "prop-types";
import { ttsService } from "../../services/textToSpeech";
import { useLanguage } from "../../context/LanguageContext";

/**
 * Botón para leer texto en voz alta usando TTS
 * Muestra un icono de altavoz que se anima cuando está hablando
 */
export function SpeakButton({ text, className = "", size = "md", onClick }) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const { language, t } = useLanguage();

  useEffect(() => {
    return () => { ttsService.stop(); };
  }, []);

  const handleSpeak = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (isSpeaking) {
      ttsService.stop();
      setIsSpeaking(false);
    } else {
      ttsService.speak(text, {
        lang: language,
        onStart: () => setIsSpeaking(true),
        onEnd:   () => setIsSpeaking(false),
      });
    }

    if (onClick) onClick(e);
  };

  const sizes = {
    xs: { button: "w-6 h-6",   icon: "w-3 h-3" },
    sm: { button: "w-8 h-8",   icon: "w-4 h-4" },
    md: { button: "w-10 h-10", icon: "w-5 h-5" },
    lg: { button: "w-12 h-12", icon: "w-6 h-6" },
  };

  const buttonSize = sizes[size] || sizes.md;

  return (
    <motion.button
      onClick={handleSpeak}
      className={`${buttonSize.button} flex items-center justify-center rounded-full bg-primary hover:bg-primary-dark transition-colors duration-200 shadow-md ${className}`}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      title={isSpeaking ? t('common.speak.stop') : t('common.speak.listen')}
    >
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="white"
        className={buttonSize.icon}
        animate={isSpeaking ? { scale: [1, 1.2, 1] } : { scale: 1 }}
        transition={isSpeaking ? { duration: 0.5, repeat: Infinity, ease: "easeInOut" } : {}}
      >
        {isSpeaking ? (
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M5.25 7.5A2.25 2.25 0 017.5 5.25h9a2.25 2.25 0 012.25 2.25v9a2.25 2.25 0 01-2.25 2.25h-9a2.25 2.25 0 01-2.25-2.25v-9z"
          />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z"
          />
        )}
      </motion.svg>
    </motion.button>
  );
}

SpeakButton.propTypes = {
  text: PropTypes.string.isRequired,
  className: PropTypes.string,
  size: PropTypes.oneOf(["xs", "sm", "md", "lg"]),
  onClick: PropTypes.func,
};
